import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { WindowMessagesActions } from '../window-messages/window-messages.actions';
import { map, of, switchMap } from 'rxjs';
import { VaButtonActions } from './va-button.actions';
import { selectVaButtonState } from './va-button.reducer';
import { WindowStateActions } from '../window/window.actions';
import { SettingsActions } from '../settings/settings.action';
import { TranslateService } from '@ngx-translate/core';
import { ChatHeaderActions } from '../chat-header/chat-header.actions';
import { getChatHeaderLogoImage } from '../../../utils/getChatHeaderLogoImage';
import { getBrand } from '../../../utils/getBrand';
import { selectReceivedFirstOperatorMessage } from '../live-chat/live-chat.reducer';
import { WindowMessage } from '../../types/enum';
import { MainComponentActions } from '../main.actions';
import { AuthTokenActions } from '../auth-token/auth-token.actions';
import { selectWindowStateState } from '../window/window.reducer';

@Injectable({
  providedIn: 'root',
})
export class VaButtonEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private translateService: TranslateService
  ) {}

  sendUpdateVaStoreMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MainComponentActions.mainComponentLoaded,
        WindowStateActions.openWindow,
        AuthTokenActions.startSession,
        WindowMessagesActions.onAppRefresh,
        VaButtonActions.minimize,
        WindowStateActions.closeWindow,
        VaButtonActions.onSessionExpired,
        SettingsActions.setLanguageSuccess,
        VaButtonActions.setButtonText,
        VaButtonActions.updateUnreadMessageCount,
        VaButtonActions.enableOrDisableFloatingButton,
        ChatHeaderActions.setHeader,
        ChatHeaderActions.setLogo,
        VaButtonActions.setLogoUrl,
        VaButtonActions.setMinimizedHeaderTitle,
        VaButtonActions.setChatAppBrand,
        VaButtonActions.setProactiveChat
      ),
      concatLatestFrom(() => this.store.select(selectVaButtonState)),
      switchMap(([, vaButtonState]) =>
        of(
          WindowMessagesActions.sendWindowMessage({
            message: WindowMessage.UPDATE_VA_STORE,
            messageData: vaButtonState,
          })
        )
      )
    )
  );
  updateButtonText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.setLanguageSuccess),
      switchMap(() => {
        const buttonText = this.translateService.instant(
          'FLOATING_BUTTON_TEXT'
        );
        return of(VaButtonActions.setButtonText({ buttonText }));
      })
    )
  );

  updateMinimizedHeaderTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.setLanguageSuccess),
      switchMap(() => {
        const minimizedHeaderTitle = this.translateService.instant('TITLE');
        return of(
          VaButtonActions.setMinimizedHeaderTitle({ minimizedHeaderTitle })
        );
      })
    )
  );

  setChatAppBrand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.initializeApp),
      map(() =>
        VaButtonActions.setChatAppBrand({ chatAppBrand: getBrand().name })
      )
    )
  );

  setlVaLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        WindowStateActions.closeWindow,
        WindowMessagesActions.initializeApp
      ),
      concatLatestFrom(() => [
        this.store.select(selectReceivedFirstOperatorMessage),
        this.store.select(selectWindowStateState),
      ]),
      switchMap(([, isConnected, windowState]) => {
        const logoImageUrl = getChatHeaderLogoImage(
          isConnected,
          getBrand().code,
          windowState
        );
        return of(
          VaButtonActions.setLogoUrl({
            logoImageUrl,
          })
        );
      })
    )
  );
}
